import { Close as CloseIcon } from "@mui/icons-material";
import { AppBar, Dialog, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AsolviSwitch } from "components/AsolviSwitch";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { setOpen as setDialogOpen } from "store/slices/dialog.store";
import { setServerSavedVisitData } from "store/slices/user.store";

const PREFIX = "ServerSavedVisitDataSettingsDialog";

const classes = {
  menuButton: `${PREFIX}-menuButton`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
    color: theme.palette.common.white,
  },
}));

interface ServerSavedVisitDataSettingsProps {}

export const ServerSavedVisitDataSettingsDialog: FC<ServerSavedVisitDataSettingsProps> = () => {
  const dispatch = useAppDispatch();
  const { serverSavedVisitData } = useAppSelector((state) => state.user);
  const { serverSavedVisitData: open } = useAppSelector((state) => state.dialog);
  const setOpen = useCallback(
    (open: boolean) => {
      dispatch(setDialogOpen({ dialogName: "serverSavedVisitData", open }));
    },
    [dispatch],
  );
  const intl = useIntl();

  const toggleServerSavedVisitData = useCallback(() => {
    localStorage.setItem("serverSavedVisitData", JSON.stringify(!serverSavedVisitData));
    dispatch(setServerSavedVisitData(!serverSavedVisitData));
  }, [dispatch, serverSavedVisitData]);

  return (
    <StyledDialog
      fullScreen
      open={open}
      onClose={() => setOpen(false)}
      TransitionComponent={Transition}
    >
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          <IconButton
            aria-label={intl.formatMessage({ id: "general.close" })}
            className={classes.menuButton}
            onClick={() => setOpen(false)}
            data-testid="ServerSavedVisitDataSettingsDialog-CloseButton"
            size="large"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Typography flexGrow={1}>
            <FormattedMessage id="menu.serverSavedVisitDataSettings" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />

      <StyledContainer>
        <Grid item>
          <Typography variant="body1">
            <FormattedMessage id="storedVisitData.details" />
          </Typography>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ paddingTop: "20px", paddingBottom: "20px" }}
        >
          <Grid item>
            <Typography variant="body1">
              <FormattedMessage id="storedVisitData.toggleText" />
            </Typography>
          </Grid>
          <Grid item>
            <AsolviSwitch
              key="autoSyncDocuments"
              checked={serverSavedVisitData}
              onClick={() => {
                toggleServerSavedVisitData();
              }}
              data-testid="ServerSavedVisitDataSettingsDialog-Toggle"
            />
          </Grid>
        </Grid>
      </StyledContainer>
    </StyledDialog>
  );
};
